import { isPlatformBrowser, isPlatformServer } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import {
  computed,
  inject,
  Injectable,
  makeStateKey,
  PLATFORM_ID,
  REQUEST,
  signal,
  TransferState,
} from '@angular/core';
import { firstValueFrom, Observable, of, switchMap } from 'rxjs';
import { environment } from '../../../environments';
import {
  CachedContent,
  ContactDetails,
  Country,
  Currency,
  GlobalRegion,
  ImsiIndex,
  Language,
  Region,
} from '../../interfaces/cached-content';
import { THEME_SIGNAL } from '../../interfaces/theme';
import { CACHE_WARMUP_SERVICE_TOKEN, REDIS_CLIENT_TOKEN } from '../../tokens/server-tokens';
import { ApiConfigService } from '../api-config/api-config.service';

const CACHE_CONTENT_STATE_KEY = makeStateKey<CachedContent>('CACHE_CONTENT_STATE_KEY');

export const DEFAULT_CURRENCY = {
  _id: '',
  currency: 'USD',
  currency_name: 'United States dollar',
} as Currency;

export const DEFAULT_LANGUAGE = {
  _id: '',
  code: 'en',
  name: 'English',
  flag: '',
  flagUrl: '',
  RTL: false,
} as Language;

@Injectable({
  providedIn: 'root',
})
export class CacheService {
  protected redisClient = inject(REDIS_CLIENT_TOKEN, { optional: true });
  protected cacheWarmup = inject(CACHE_WARMUP_SERVICE_TOKEN, { optional: true });

  protected platform = inject(PLATFORM_ID);

  protected request = inject(REQUEST);

  public cachedContent = signal<null | CachedContent>(null);

  protected http = inject(HttpClient);

  protected apiConfig = inject(ApiConfigService);

  protected transferState = inject(TransferState);

  private readonly theme = inject(THEME_SIGNAL);

  public defaultLanguage = computed<Language>(() => {
    const cacheContent = this.cachedContent();
    if (!cacheContent) {
      return DEFAULT_LANGUAGE;
    }

    // Fallback to English when not specified in config (e.g. flag)
    const defaultLanguageCode =
      cacheContent.cache.config.languages.default || DEFAULT_LANGUAGE.code;

    return (
      cacheContent.cache.config.languages.supported.find(
        (language) => language.code === defaultLanguageCode
      ) || DEFAULT_LANGUAGE
    );
  });

  public currencyConversionMasterEnabled = computed<boolean>(() => {
    return this.cachedContent()?.cache.config.currencyConversionMasterEnabled ?? false;
  });

  public defaultCurrency = computed<Currency>(() => {
    const cachedContent = this.cachedContent();

    if (!cachedContent) {
      return DEFAULT_CURRENCY;
    }

    const { currencyObj, currency, currencyConversionMasterEnabled } = cachedContent.cache.config;

    const findCurrency = (code: string | undefined): Currency | undefined =>
      code ? currencyObj.currencyList.find((value) => value.currency === code) : undefined;

    if (!currencyConversionMasterEnabled) {
      const configCurrency = findCurrency(currency);
      if (configCurrency) {
        return configCurrency;
      }
    }

    const fromDefaultRef = findCurrency(currencyObj.defaultCurrency?.currency);
    return fromDefaultRef ?? DEFAULT_CURRENCY;
  });

  public allCountries = computed<Country[]>(() => {
    const cacheContent = this.cachedContent();

    if (!cacheContent) {
      return [];
    }

    return cacheContent.cache.singleCountries;
  });

  public allRegions = computed<Region[]>(() => {
    const cacheContent = this.cachedContent();

    if (!cacheContent) {
      return [];
    }

    return cacheContent.cache.regions;
  });

  public imsiIndexes = computed<ImsiIndex>(() => {
    const cacheContent = this.cachedContent();

    if (!cacheContent) {
      return {};
    }

    return cacheContent.cache.imsiIndexes;
  });

  public globalRegions = computed<GlobalRegion[]>(() => {
    const cacheContent = this.cachedContent();

    if (!cacheContent) {
      return [];
    }

    return cacheContent.cache.globalRegions;
  });

  public allLanguages = computed<Language[]>(() => {
    const cacheContent = this.cachedContent();

    if (!cacheContent) {
      return [];
    }

    return cacheContent.cache.config.languages.supported;
  });

  public contactDetails = computed<ContactDetails | null>(() => {
    const cacheContent = this.cachedContent();

    if (!cacheContent) {
      return null;
    }

    return cacheContent.cache.config.contactDetails ?? null;
  });

  public billingAddress = computed(() => {
    const cacheContent = this.cachedContent();
    return cacheContent?.cache.config.billingAddress ?? null;
  });

  public faviconUrl = computed<string>(() => {
    const cacheContent = this.cachedContent();

    return cacheContent?.cache.config.webAppConfig.favicon ?? '';
  });

  public oneClickInstallAndroidEnabled = computed<boolean>(() => {
    const cacheContent = this.cachedContent();
    return cacheContent?.cache.config.oneClickInstallAndroidEnabled ?? false;
  });

  constructor() {
    this.initCacheBrowser();
  }

  public initCacheBrowser() {
    if (isPlatformBrowser(this.platform)) {
      this.cachedContent.set(this.transferState.get(CACHE_CONTENT_STATE_KEY, null));
      this.transferState.remove(CACHE_CONTENT_STATE_KEY);
      if (!environment.env.startsWith('prod-')) {
        console.log(this.cachedContent());
      }
    }
  }

  public retrieveCacheFromServer(host: string, cacheId?: string): Observable<CachedContent> {
    return this.http
      .get<CachedContent | { message: string }>(this.apiConfig.url('cache'), {
        params: {
          host,
          platform: environment.platform,
          version: environment.version,
          appVersion: environment.version,
          cacheId: cacheId || '',
        },
      })
      .pipe(
        switchMap((cachedContent) => {
          if (!this.checkCacheContent(cachedContent)) {
            throw new Error(`Cache not set for the host ${host} in the backend. System failure`);
          }

          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { androidConfig, shopifyAppConfig, ...config } = cachedContent.cache.config;

          const minimizedCacheContent = {
            cacheId: cachedContent.cacheId,
            cache: {
              customerId: cachedContent.cache.customerId,
              config,
              singleCountries: cachedContent.cache.singleCountries,
              regions: cachedContent.cache.regions,
              globalRegions: cachedContent.cache.globalRegions,
              imsiIndexes: cachedContent.cache.imsiIndexes,
            },
          } as CachedContent;

          if (this.redisClient) {
            this.redisClient
              .setKey(host, minimizedCacheContent)
              .then((value) =>
                console.log(`Cache update for host ${host} was successful: ${value}`)
              );
          }

          return of(minimizedCacheContent);
        })
      );
  }

  public async initCacheInServer() {
    if (!isPlatformServer(this.platform)) {
      throw new Error('Caching can only be initialized in Server');
    }

    const host = this.getHost();

    // Check memory cache first (from CacheWarmupService) - FAST (~1ms)
    if (this.cacheWarmup) {
      const cached = this.cacheWarmup.getCache(host) as CachedContent | null;
      if (cached) {
        console.log(`✅ Cache retrieved from memory cache for ${host}`);
        this.cachedContent.set(cached);
        this.transferState.set(CACHE_CONTENT_STATE_KEY, cached);
        return cached;
      }
    }

    // Fallback to Redis/HTTP (should rarely happen if warmup worked)
    if (!this.redisClient) {
      throw new Error('Redis client not available in server mode');
    }

    let cachedContent = await this.redisClient.getKey<CachedContent>(host);

    cachedContent = cachedContent ?? (await firstValueFrom(this.retrieveCacheFromServer(host)));

    this.cachedContent.set(cachedContent);

    this.transferState.set(CACHE_CONTENT_STATE_KEY, cachedContent);

    return cachedContent;
  }

  public getHost(): string {
    if (!this.request) {
      throw new Error('Request not available in server mode');
    }

    const host = this.request.headers.get('host');

    if (!host) {
      throw new Error('Host not set in server mode');
    }

    // we are still using hard coded content for host
    // return host.includes('localhost') || host.includes('127.0.0.1')
    //   ? 'yez.dev-app.glowingbud.xyz'
    //   : host;
    if (host.includes('localhost') || host.includes('127.0.0.1')) {
      if (this.theme()) {
        return `9t3cir0g0ew1.glowingbud.net`;
      }

      return '9t3cir0g0ew1.glowingbud.net';
    }

    return host;
  }

  protected checkCacheContent(
    cachedContent: CachedContent | { message: string }
  ): cachedContent is CachedContent {
    return !(
      ('message' in cachedContent && cachedContent.message === 'Cache not updated') ||
      !('cache' in cachedContent && 'cacheId' in cachedContent)
    );
  }
}
